import { Component, ElementRef, Input, OnDestroy, TemplateRef, ViewChild, ViewContainerRef } from '@angular/core';
import { FormControl } from '@angular/forms';
import { ConnectedPosition, Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';

const PICKER_ANCHOR_OFFSET_PX = 4;
const PICKER_VIEWPORT_MARGIN_PX = 8;

@Component({
  selector: 'rnpl-gl-category-icon-picker',
  templateUrl: './gl-category-icon-picker.component.html',
  styleUrls: ['./gl-category-icon-picker.component.scss'],
})
export class GlCategoryIconPickerComponent implements OnDestroy {

  @Input() control: FormControl = new FormControl();
  @Input() icons: string[] = [];
  @Input() emptyIcon = 'folder';

  @ViewChild('trigger', { static: true }) private triggerRef: ElementRef<HTMLElement>;
  @ViewChild('panelTemplate', { static: true }) private panelTemplateRef: TemplateRef<any>;

  private overlayRef: OverlayRef;

  constructor(private overlay: Overlay, private viewContainerRef: ViewContainerRef) { }

  public get isOpen(): boolean {
    return !!this.overlayRef;
  }

  public toggle(): void {
    if (this.control.disabled) { return; }

    if (this.isOpen) {
      this.close();
      return;
    }

    this.open();
  }

  public select(icon: string): void {
    this.control.setValue(icon);
    this.control.markAsDirty();
    this.close();
  }

  public close(): void {
    if (this.overlayRef) {
      this.overlayRef.dispose();
      this.overlayRef = null;
    }
  }

  ngOnDestroy(): void {
    this.close();
  }

  private open(): void {
    const positionStrategy = this.overlay.position()
      .flexibleConnectedTo(this.triggerRef.nativeElement)
      .withPositions(this.buildPositions())
      .withFlexibleDimensions(false)
      .withPush(true)
      .withViewportMargin(PICKER_VIEWPORT_MARGIN_PX);

    this.overlayRef = this.overlay.create({
      positionStrategy,
      scrollStrategy: this.overlay.scrollStrategies.reposition(),
      hasBackdrop: true,
      backdropClass: 'cdk-overlay-transparent-backdrop',
    });

    this.overlayRef.backdropClick().subscribe(() => this.close());
    this.overlayRef.keydownEvents().subscribe((event: KeyboardEvent) => {
      if (event.key === 'Escape') { this.close(); }
    });

    this.overlayRef.attach(new TemplatePortal(this.panelTemplateRef, this.viewContainerRef));
  }

  private buildPositions(): ConnectedPosition[] {
    return [
      { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: PICKER_ANCHOR_OFFSET_PX },
      { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetY: -PICKER_ANCHOR_OFFSET_PX },
    ];
  }
}
